import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import {
    ArrowLeft, Shield, AlertTriangle, RefreshCw, CheckCircle, XCircle,
    UserX, Lock, Activity
} from 'lucide-react';

const SEVERITY_STYLES = {
    info:     { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-400',    label: 'INFO' },
    low:      { bg: 'bg-slate-500/10',   border: 'border-slate-500/30',   text: 'text-slate-300',   label: 'LOW' },
    medium:   { bg: 'bg-yellow-500/10',  border: 'border-yellow-500/30',  text: 'text-yellow-400',  label: 'MEDIUM' },
    high:     { bg: 'bg-orange-500/10',  border: 'border-orange-500/30',  text: 'text-orange-400',  label: 'HIGH' },
    critical: { bg: 'bg-red-500/15',     border: 'border-red-500/40',     text: 'text-red-400',     label: 'CRITICAL' },
};

const EVENT_ICONS = {
    login_success:           CheckCircle,
    login_failed:            XCircle,
    login_blocked_inactive:  UserX,
    admin_access_denied:     Lock,
    rate_limit_exceeded:     AlertTriangle,
};

const EVENT_LABELS = {
    login_success:           'Login riuscito',
    login_failed:            'Login fallito',
    login_blocked_inactive:  'Login bloccato (account disabilitato)',
    admin_access_denied:     'Accesso admin negato',
    rate_limit_exceeded:     'Rate limit superato',
};

const formatDate = (iso) => {
    if (!iso) return '-';
    try {
        const d = new Date(iso);
        return d.toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' });
    } catch { return iso; }
};

export default function SecurityEvents() {
    const navigate = useNavigate();
    const [data, setData] = useState({ events: [], stats_24h: { by_type: {}, by_severity: {}, total: 0 } });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        severity: '',
        event_type: '',
        since_hours: 24,
        limit: 100,
    });
    const [autoRefresh, setAutoRefresh] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {};
            if (filters.severity) params.severity = filters.severity;
            if (filters.event_type) params.event_type = filters.event_type;
            if (filters.since_hours) params.since_hours = filters.since_hours;
            if (filters.limit) params.limit = filters.limit;
            const res = await api.get('/admin/security-events', { params });
            setData(res.data);
        } catch (err) {
            setError(err?.response?.data?.detail || 'Errore caricamento eventi');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!autoRefresh) return;
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [autoRefresh, load]);

    return (
        <div className="min-h-screen bg-[#0A0A0A] text-white pb-12">
            <div className="border-b border-white/10 bg-[#111] sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/admin')}
                            className="text-white/60 hover:text-white"
                            data-testid="security-events-back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-1" />
                            Admin
                        </Button>
                        <div className="h-6 w-px bg-white/10" />
                        <h1 className="text-lg font-bold flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#00FF41]" />
                            Eventi di Sicurezza
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                data-testid="auto-refresh-toggle"
                            />
                            Auto-refresh 15s
                        </label>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={load}
                            disabled={loading}
                            className="border-white/20 text-white"
                            data-testid="security-events-refresh-btn"
                        >
                            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                            Aggiorna
                        </Button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
                {/* Stats 24h */}
                <section data-testid="security-stats-24h">
                    <h2 className="text-sm font-mono uppercase tracking-widest text-white/40 mb-3">
                        Statistiche ultime 24 ore — totale: {data.stats_24h.total}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {Object.entries(data.stats_24h.by_severity).map(([sev, count]) => {
                            const st = SEVERITY_STYLES[sev] || SEVERITY_STYLES.info;
                            return (
                                <div key={sev} className={`${st.bg} ${st.border} border p-4`}>
                                    <div className={`text-xs font-mono uppercase tracking-widest ${st.text}`}>{st.label}</div>
                                    <div className="text-3xl font-bold mt-1">{count}</div>
                                </div>
                            );
                        })}
                        {data.stats_24h.total === 0 && (
                            <div className="col-span-full text-white/40 text-sm italic">
                                Nessun evento nelle ultime 24 ore.
                            </div>
                        )}
                    </div>
                </section>

                {/* Filters */}
                <section className="bg-[#111] border border-white/10 p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <label className="block text-xs text-white/40 uppercase mb-1">Severity</label>
                        <select
                            value={filters.severity}
                            onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value }))}
                            className="w-full bg-black border border-white/20 px-3 py-2 text-sm"
                            data-testid="filter-severity"
                        >
                            <option value="">Tutte</option>
                            <option value="info">Info</option>
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-white/40 uppercase mb-1">Tipo Evento</label>
                        <select
                            value={filters.event_type}
                            onChange={(e) => setFilters(f => ({ ...f, event_type: e.target.value }))}
                            className="w-full bg-black border border-white/20 px-3 py-2 text-sm"
                            data-testid="filter-event-type"
                        >
                            <option value="">Tutti</option>
                            {Object.entries(EVENT_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-white/40 uppercase mb-1">Periodo</label>
                        <select
                            value={filters.since_hours}
                            onChange={(e) => setFilters(f => ({ ...f, since_hours: Number(e.target.value) }))}
                            className="w-full bg-black border border-white/20 px-3 py-2 text-sm"
                            data-testid="filter-since"
                        >
                            <option value="1">Ultima ora</option>
                            <option value="24">Ultime 24h</option>
                            <option value="168">Ultima settimana</option>
                            <option value="720">Ultimo mese (max)</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-white/40 uppercase mb-1">Limite</label>
                        <select
                            value={filters.limit}
                            onChange={(e) => setFilters(f => ({ ...f, limit: Number(e.target.value) }))}
                            className="w-full bg-black border border-white/20 px-3 py-2 text-sm"
                            data-testid="filter-limit"
                        >
                            <option value="50">50</option>
                            <option value="100">100</option>
                            <option value="200">200</option>
                            <option value="500">500</option>
                        </select>
                    </div>
                </section>

                {/* Errori */}
                {error && (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 text-sm">
                        {error}
                    </div>
                )}

                {/* Tabella eventi */}
                <section className="bg-[#111] border border-white/10 overflow-x-auto" data-testid="security-events-table">
                    <table className="w-full text-sm">
                        <thead className="bg-black/40 border-b border-white/10">
                            <tr className="text-left font-mono uppercase text-xs text-white/40">
                                <th className="px-4 py-3">Quando</th>
                                <th className="px-4 py-3">Severity</th>
                                <th className="px-4 py-3">Evento</th>
                                <th className="px-4 py-3">Utente</th>
                                <th className="px-4 py-3">IP</th>
                                <th className="px-4 py-3">User Agent</th>
                                <th className="px-4 py-3">Dettagli</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && data.events.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-8 text-center text-white/40">Caricamento…</td></tr>
                            )}
                            {!loading && data.events.length === 0 && (
                                <tr><td colSpan="7" className="px-4 py-8 text-center text-white/40">
                                    <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    Nessun evento trovato con i filtri attuali.
                                </td></tr>
                            )}
                            {data.events.map(ev => {
                                const st = SEVERITY_STYLES[ev.severity] || SEVERITY_STYLES.info;
                                const Icon = EVENT_ICONS[ev.event_type] || Activity;
                                return (
                                    <tr key={ev.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                                        <td className="px-4 py-3 font-mono text-xs text-white/60 whitespace-nowrap">
                                            {formatDate(ev.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 text-xs font-mono ${st.bg} ${st.text} border ${st.border}`}>
                                                {st.label}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Icon className={`w-4 h-4 ${st.text}`} />
                                                {EVENT_LABELS[ev.event_type] || ev.event_type}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-white/80">
                                            {ev.actor_email || <span className="text-white/30 italic">anonimo</span>}
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs text-white/60">{ev.ip || '-'}</td>
                                        <td className="px-4 py-3 text-xs text-white/40 max-w-xs truncate" title={ev.user_agent}>
                                            {ev.user_agent || '-'}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-white/50 font-mono max-w-xs truncate" title={JSON.stringify(ev.details)}>
                                            {ev.details && Object.keys(ev.details).length > 0
                                                ? Object.entries(ev.details).map(([k, v]) => `${k}=${v}`).join(' · ')
                                                : '-'}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </section>

                <p className="text-xs text-white/30 text-center">
                    Eventi persistiti per 90 giorni (TTL automatico). Pulizia su <code>created_at</code>.
                </p>
            </div>
        </div>
    );
}
